import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { usePageContext } from "../contexts/PageContext";
import FreemiumJourneyHeader from "../components/freemium/FreemiumJourneyHeader";
import {
  getActiveSubscription,
  cancelSubscription,
  getPaymentMethod,
  getInvoices
} from "../services/subscriptionService";
import {
  Loader2,
  AlertCircle,
  CreditCard,
  Calendar,
  CheckCircle2,
  XCircle,
  Download,
  ExternalLink,
  X
} from "lucide-react";

interface Subscription {
  id: string;
  client_id: string;
  entitlement_id: string;
  plan_id: string;
  plan_name: string;
  billing_cycle: string;
  status: string;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  grace_period_ends_at: string | null;
  stripe_subscription_id: string;
  stripe_customer_id: string;
  stripe_price_id: string;
  created_at: string;
  updated_at: string;
}

interface PaymentMethod {
  payment_method: {
    id: string;
    type: string;
    card: {
      brand: string;
      last4: string;
      exp_month: number;
      exp_year: number;
    };
  };
}

interface Invoice {
  id: string;
  number: string;
  amount_due: number;
  amount_paid: number;
  currency: string;
  status: string;
  created: number;
  hosted_invoice_url: string;
  invoice_pdf: string;
}

export default function SubscriptionManagementPage() {
  const { currentUser, session, sessionLoading } = useAuth();
  const { setPageContext } = usePageContext();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  // Set page context - empty breadcrumbs to hide global header tabs
  useEffect(() => {
    setPageContext({
      breadcrumbs: [],
      tabs: ["Tools", "Documents", "Messages"]
    });
  }, [setPageContext]);

  const fetchData = async () => {
    if (!session?.client_id || !currentUser) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get Firebase auth token
      const token = await currentUser.getIdToken();

      const [subData, pmData, invoiceData] = await Promise.all([
        getActiveSubscription(session.client_id, token),
        getPaymentMethod(session.client_id, token).catch(() => null),
        getInvoices(session.client_id, token, 10).catch(() => ({ invoices: [] }))
      ]);

      setSubscription(subData);
      setPaymentMethod(pmData);
      setInvoices(invoiceData.invoices || []);
    } catch (err) {
      console.error('Failed to load subscription:', err);
      setError('Failed to load subscription details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!sessionLoading && currentUser) {
      fetchData();
    }
  }, [session?.client_id, sessionLoading, currentUser]);

  const handleCancelSubscription = async () => {
    if (!session?.client_id || !currentUser) return;

    try {
      setCanceling(true);
      setCancelError(null);

      // Get Firebase auth token
      const token = await currentUser.getIdToken();

      await cancelSubscription(session.client_id, token, false);
      setCancelModalOpen(false);
      await fetchData();
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
      setCancelError('Failed to cancel subscription. Please try again.');
    } finally {
      setCanceling(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatUnixDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount / 100);
  };

  // Fallback function for plan names when plan_name is not available in API response
  const getPlanName = (planId: string) => {
    switch (planId) {
      case 'TIER_2':
        return 'DVA Professional';
      case 'TIER_1':
        return 'DVA Free';
      default:
        return `DVA ${planId}`;
    }
  };

  const getBillingCycleLabel = (cycle: string) => {
    switch (cycle) {
      case 'MONTHLY':
        return 'Monthly';
      case 'ANNUAL':
        return 'Annual';
      default:
        return cycle;
    }
  };

  const getCardBrandIcon = (brand: string) => {
    const brandLower = brand.toLowerCase();
    if (brandLower === 'visa') return '💳';
    if (brandLower === 'mastercard') return '💳';
    if (brandLower === 'amex') return '💳';
    return '💳';
  };

  const getStatusBadge = (status: string, cancelAtPeriodEnd: boolean) => {
    if (cancelAtPeriodEnd) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
          <AlertCircle className="w-4 h-4" />
          Cancels at period end
        </span>
      );
    }

    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'active':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
            <CheckCircle2 className="w-4 h-4" />
            Active
          </span>
        );
      case 'canceled':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
            <XCircle className="w-4 h-4" />
            Canceled
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-800">
            {status}
          </span>
        );
    }
  };

  const getInvoiceStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'paid':
        return <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">Paid</span>;
      case 'open':
        return <span className="px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">Open</span>;
      case 'void':
        return <span className="px-2 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-800">Void</span>;
      default:
        return <span className="px-2 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-800">{status}</span>;
    }
  };

  if (sessionLoading || loading) {
    return (
      <main className="max-w-[1440px] mx-auto px-6 pb-4">
        <FreemiumJourneyHeader
          stepNumber={undefined}
          stepName="Subscription Management"
          description="View and manage your DVA Pro subscription, billing details, and payment information."
        />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#FF6E4C] mx-auto mb-3" />
            <p className="text-sm text-[#717182]">Loading subscription...</p>
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="max-w-[1440px] mx-auto px-6 pb-4">
        <FreemiumJourneyHeader
          stepNumber={undefined}
          stepName="Subscription Management"
          description="View and manage your DVA Pro subscription, billing details, and payment information."
        />
        <div className="rounded-2xl bg-red-50 ring-1 ring-red-200 p-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-red-900 mb-1">Error</h3>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      </main>
    );
  }

  if (!subscription) {
    return (
      <main className="max-w-[1440px] mx-auto px-6 pb-4">
        <FreemiumJourneyHeader
          stepNumber={undefined}
          stepName="Subscription Management"
          description="View and manage your DVA Pro subscription, billing details, and payment information."
        />
        <div className="rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm p-8 text-center">
          <AlertCircle className="w-16 h-16 text-[#717182] mx-auto mb-4" />
          <h3 className="text-xl font-bold text-[#18365E] mb-2">No Active Subscription</h3>
          <p className="text-sm text-[#717182]">You don't have an active subscription.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="max-w-[1440px] mx-auto px-6 pb-4">
      {/* Page Header */}
      <FreemiumJourneyHeader
        stepNumber={undefined}
        stepName="Subscription Management"
        description={`View and manage your ${subscription.plan_name || 'DVA'} subscription, billing details, and payment information.`}
      />

      {/* Content */}
      <div className="mb-6" />

      {/* Main Subscription Card */}
      <section className="rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm overflow-hidden mb-6">
        <div className="bg-gradient-to-r from-[#18365E] to-[#2a4d7d] p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h2 className="text-2xl font-bold text-white mb-1">{subscription.plan_name || getPlanName(subscription.plan_id)}</h2>
              <p className="text-sm text-blue-100 font-medium">{getBillingCycleLabel(subscription.billing_cycle)} billing</p>
            </div>
            {getStatusBadge(subscription.status, subscription.cancel_at_period_end)}
          </div>
        </div>

        <div className="p-6 grid md:grid-cols-2 gap-6">
          {/* Billing Period */}
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Calendar className="w-5 h-5 text-[#18365E]" />
            </div>
            <div>
              <h3 className="font-bold text-[#18365E] mb-1">Current Billing Period</h3>
              <p className="text-sm text-[#475569] mb-1">
                {formatDate(subscription.current_period_start)} - {formatDate(subscription.current_period_end)}
              </p>
              {!subscription.cancel_at_period_end && (
                <p className="text-xs text-[#717182]">
                  Renews on {formatDate(subscription.current_period_end)}
                </p>
              )}
            </div>
          </div>

          {/* Payment Method */}
          <div className="flex items-start gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <CreditCard className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-bold text-[#18365E] mb-1">Payment Method</h3>
              {paymentMethod?.payment_method?.card ? (
                <div className="text-sm text-[#475569]">
                  <p className="flex items-center gap-2 mb-1">
                    <span>{getCardBrandIcon(paymentMethod.payment_method.card.brand)}</span>
                    <span className="capitalize font-medium">{paymentMethod.payment_method.card.brand}</span>
                    <span>•••• {paymentMethod.payment_method.card.last4}</span>
                  </p>
                  <p className="text-xs text-[#717182]">
                    Expires {paymentMethod.payment_method.card.exp_month}/{paymentMethod.payment_method.card.exp_year}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-[#717182]">No payment method on file</p>
              )}
            </div>
          </div>
        </div>

        {/* Warning if canceling */}
        {subscription.cancel_at_period_end && (
          <div className="mx-6 mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-amber-900 mb-1">Subscription Canceling</h4>
              <p className="text-sm text-amber-700">
                Your subscription will end on {formatDate(subscription.current_period_end)}.
                You'll have access to Pro features until then.
              </p>
            </div>
          </div>
        )}

        {/* Cancel Button */}
        {subscription.status.toLowerCase() === 'active' && !subscription.cancel_at_period_end && (
          <div className="px-6 pb-6">
            <button
              onClick={() => setCancelModalOpen(true)}
              className="text-sm text-red-600 hover:text-red-700 hover:underline font-medium"
            >
              Cancel subscription
            </button>
          </div>
        )}
      </section>

      {/* Billing History */}
      {invoices.length > 0 && (
        <section className="rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm p-6 mb-6">
          <h3 className="text-xl font-bold text-[#18365E] mb-4">Billing History</h3>
          <div className="space-y-3">
            {invoices.map((invoice) => (
              <div key={invoice.id} className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 py-3 border-b border-slate-100 last:border-0">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <p className="font-bold text-[#18365E]">
                      {invoice.number || `Invoice ${invoice.id.substring(0, 8)}`}
                    </p>
                    {getInvoiceStatusBadge(invoice.status)}
                  </div>
                  <p className="text-sm text-[#717182]">
                    {formatUnixDate(invoice.created)}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <p className="text-lg font-bold text-[#18365E]">
                    {formatCurrency(invoice.amount_paid, invoice.currency)}
                  </p>
                  <div className="flex gap-2">
                    {invoice.hosted_invoice_url && (
                      <a
                        href={invoice.hosted_invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-[#717182] hover:text-[#18365E] hover:bg-slate-50 rounded-lg transition-colors"
                        title="View invoice"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                    {invoice.invoice_pdf && (
                      <a
                        href={invoice.invoice_pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-[#717182] hover:text-[#18365E] hover:bg-slate-50 rounded-lg transition-colors"
                        title="Download PDF"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Subscription Details */}
      <section className="rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm p-6">
        <h3 className="text-xl font-bold text-[#18365E] mb-4">Subscription Details</h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center py-2 border-b border-slate-100">
            <span className="text-sm text-[#717182] font-medium">Subscription ID</span>
            <span className="text-sm text-[#18365E] font-mono font-semibold">{subscription.id}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-slate-100">
            <span className="text-sm text-[#717182] font-medium">Plan</span>
            <span className="text-sm text-[#18365E] font-semibold">{subscription.plan_name || getPlanName(subscription.plan_id)}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-slate-100">
            <span className="text-sm text-[#717182] font-medium">Created</span>
            <span className="text-sm text-[#18365E] font-semibold">{formatDate(subscription.created_at)}</span>
          </div>
          <div className="flex justify-between items-center py-2">
            <span className="text-sm text-[#717182] font-medium">Last Updated</span>
            <span className="text-sm text-[#18365E] font-semibold">{formatDate(subscription.updated_at)}</span>
          </div>
        </div>
      </section>

      {/* Cancel Confirmation Modal */}
      {cancelModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-[#18365E]">Cancel Subscription</h3>
              </div>
              <button
                onClick={() => setCancelModalOpen(false)}
                className="text-[#717182] hover:text-[#18365E]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-[#475569] mb-6">
              Are you sure you want to cancel your subscription? You'll continue to have access to Pro features until {formatDate(subscription.current_period_end)}.
            </p>

            {cancelError && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                {cancelError}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setCancelModalOpen(false)}
                disabled={canceling}
                className="flex-1 px-4 py-3 border border-slate-300 text-[#18365E] rounded-xl font-semibold hover:bg-slate-50 disabled:opacity-50 transition-all"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={canceling}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-all"
              >
                {canceling ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Canceling...
                  </>
                ) : (
                  'Cancel Subscription'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
